import { ProCard } from '@components'
import proCardStyles from '@components/DataDisplay/ProCard/ProCard.module.scss'
import cn from '@integrations/classnames'
import { createContext, type ReactNode, useContext } from '@integrations/react'
import { uiSize } from '@utils'
import styles from './ListCard.module.scss'

interface ListCardContextValue {
  isLoading: boolean
}

const ListCardContext = createContext<ListCardContextValue>({ isLoading: false })

interface ListCardProps {
  isLoading: boolean
  className?: string
  'data-testid'?: string
  children: ReactNode
}

function ListCard({ isLoading, className, 'data-testid': testId, children }: ListCardProps) {
  return (
    <ListCardContext.Provider value={{ isLoading }}>
      <ProCard className={cn('list-card', className)} data-testid={testId}>
        {children}
      </ProCard>
    </ListCardContext.Provider>
  )
}

interface HeaderProps {
  title: string
  subtitle?: string
  info?: string
}

function Header({ title, subtitle, info }: HeaderProps) {
  return (
    <div className={cn(proCardStyles['pro-card__section-header'], 'pro-card__section-header')}>
      <div>
        <h3 className={cn(proCardStyles['pro-card__section-title'], 'pro-card__section-title')}>{title}</h3>
        {subtitle && <p className={cn(proCardStyles['pro-card__subtitle'], 'pro-card__subtitle')}>{subtitle}</p>}
      </div>
      {info && (
        <div className={cn(styles['list-card__info-anchor'], 'list-card__info-anchor')}>
          <svg
            width={uiSize(14)}
            height={uiSize(14)}
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
          >
            <circle cx='12' cy='12' r='10' />
            <line x1='12' y1='8' x2='12' y2='12' />
            <line x1='12' y1='16' x2='12.01' y2='16' />
          </svg>
          <div className={cn(styles['list-card__info-tooltip'], 'list-card__info-tooltip')}>{info}</div>
        </div>
      )}
    </div>
  )
}

interface BodyProps {
  skeleton: ReactNode
  isEmpty: boolean
  emptyMessage: string
  emptyAction?: ReactNode
  children: ReactNode
}

function Body({ skeleton, isEmpty, emptyMessage, emptyAction, children }: BodyProps) {
  const { isLoading } = useContext(ListCardContext)

  return (
    <div className={cn(proCardStyles['pro-card__content'], 'pro-card__content')}>
      {isLoading ? (
        skeleton
      ) : isEmpty ? (
        <div className={cn(styles['list-card__empty-state'], 'list-card__empty-state')}>
          <p className={cn(proCardStyles['pro-card__empty'], 'pro-card__empty')}>{emptyMessage}</p>
          {emptyAction}
        </div>
      ) : (
        <div className={cn(styles['list-card__scroll'], 'list-card__scroll')}>{children}</div>
      )}
    </div>
  )
}

interface FooterProps {
  label: string
  onClick: () => void
}

function Footer({ label, onClick }: FooterProps) {
  const { isLoading } = useContext(ListCardContext)

  if (isLoading) return null

  return (
    <div className={cn(proCardStyles['pro-card__footer'], 'pro-card__footer')}>
      <button
        type='button'
        className={cn(proCardStyles['pro-card__footer-link'], 'pro-card__footer-link')}
        onClick={onClick}
      >
        {label}
        <svg
          width={uiSize(12)}
          height={uiSize(12)}
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
        >
          <path d='M5 12h14M12 5l7 7-7 7' />
        </svg>
      </button>
    </div>
  )
}

ListCard.Header = Header
ListCard.Body = Body
ListCard.Footer = Footer

export default ListCard
